import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import { styled } from '@mui/material/styles';
import { TextField } from '@mui/material';
import Palette from '../../../themes/palette';

export interface OTPInputProps {
    length: number;
    value: string;
    // eslint-disable-next-line no-unused-vars
    onChange: (value: string) => void;
    // eslint-disable-next-line no-unused-vars
    validateChar?: (value: string, index: number) => boolean;
    autoFocus?: boolean;
    'data-testid'?: string;
    onEnter: () => void;
}

const OtpInputContainer = styled('div')({
    display: 'flex',
    gap: '8px'
});

const StyledTextField = styled(TextField)(() => ({
    width: '44px',
    height: '50px',
    '& .MuiInputBase-input': {
        height: '48px',
        width: '55px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        textAlign: 'center',
        fontSize: '1.15rem',
        color: Palette.primary.main
    },
    '& .MuiOutlinedInput-root': {
        width: '44px',
        height: '50px',
        border: `1px solid ${Palette.primary.main}`,
        '& fieldset': {
            borderRadius: '4px'
        },
        '&:hover fieldset': {
            border: `1px solid ${Palette.primary.main}`
        },
        '&.Mui-focused fieldset': {
            border: `1px solid ${Palette.primary.main}`
        }
    },
    '& .Mui-disabled': {
        backgroundColor: 'lightGray'
    }
}));

export const OTPInput = ({
    length = 6,
    value = '',
    onChange,
    validateChar = () => true,
    autoFocus = false,
    'data-testid': dataTestId,
    onEnter
}: OTPInputProps) => {
    const [localValue, setLocalValue] = useState<string[]>(Array(length).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    useEffect(() => {
        const valueArray = value.split('');
        setLocalValue(
            Array(length)
                .fill('')
                .map((_, index) => valueArray[index] || '')
        );
    }, [value, length]);

    useEffect(() => {
        if (autoFocus && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [autoFocus]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const newChar = e.target.value.slice(-1);

        if (newChar && !validateChar(newChar, index)) {
            return;
        }

        const newLocalValue = [...localValue];

        if (newChar) {
            newLocalValue[index] = newChar;
            setLocalValue(newLocalValue);

            if (index < length - 1) {
                setTimeout(() => {
                    inputRefs.current[index + 1]?.focus();
                }, 0);
            }
        } else if (e.target.value === '') {
            newLocalValue[index] = '';
            setLocalValue(newLocalValue);
        }

        onChange(newLocalValue.join(''));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'ArrowRight' && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'Backspace') {
            if (!localValue[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        } else if (e.key === 'Enter' && localValue.every((val) => val !== '')) {
            onEnter();
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain').trim();

        let validChars = '';
        for (let i = 0; i < Math.min(pastedData.length, length); i++) {
            if (validateChar(pastedData[i], i)) {
                validChars += pastedData[i];
            }
        }

        if (validChars) {
            const newLocalValue = Array(length).fill('');
            for (let i = 0; i < validChars.length; i++) {
                newLocalValue[i] = validChars[i];
            }

            setLocalValue(newLocalValue);
            onChange(validChars);

            const focusIndex = Math.min(validChars.length, length - 1);
            inputRefs.current[focusIndex]?.focus();
        }
    };

    return (
        <OtpInputContainer data-testid={dataTestId}>
            {Array(length)
                .fill(null)
                .map((_, index) => (
                    <StyledTextField
                        key={index}
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        value={localValue[index]}
                        onChange={(e) => handleChange(e as ChangeEvent<HTMLInputElement>, index)}
                        onKeyDown={(e) => handleKeyDown(e as KeyboardEvent<HTMLInputElement>, index)}
                        onPaste={handlePaste}
                        disabled={localValue.slice(0, index).some((val) => val === '')}
                        variant="outlined"
                        inputProps={{
                            maxLength: 1,
                            style: { textAlign: 'center' },
                            'data-testid': `${dataTestId}-input-${index}`
                        }}
                        autoComplete="off"
                    />
                ))}
        </OtpInputContainer>
    );
};
